/**
 * Billing Profile Service
 * 
 * Phase IV-A: Billing Profile & Payment Method Vaulting
 * 
 * SYSTEM CONTRACT:
 * - No charging in Phase IV-A
 * - Only card vaulting via Setup Intents
 */

// Get backend URL from environment
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Get billing profile status
 * @param {string} userId - User ID
 * @param {string} [token] - Auth token
 */
export async function getBillingProfileStatus(userId, token) {
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_URL}/api/nfm/billing/profile/status?user_id=${userId}`, {
    headers
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch billing profile status');
  }
  
  return response.json();
}

/**
 * Create or get billing profile
 * @param {string} userId - User ID
 * @param {string} [email] - User email
 * @param {string} [name] - User name
 * @param {string} [token] - Auth token
 */
export async function createBillingProfile(userId, email, name, token) {
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_URL}/api/nfm/billing/profile/create?user_id=${userId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email, name })
  });
  
  if (!response.ok) {
    throw new Error('Failed to create billing profile');
  }
  
  return response.json();
}

/**
 * Create a Setup Intent for card vaulting
 * @param {string} userId - User ID
 * @param {string} [email] - User email
 * @param {string} [name] - User name
 * @param {string} [token] - Auth token
 * @returns {Promise<{client_secret: string, setup_intent_id: string, customer_id: string, publishable_key: string, is_mock: boolean}>}
 */
export async function createSetupIntent(userId, email, name, token) {
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  try {
    const response = await fetch(`${API_URL}/api/nfm/billing/setup-intent?user_id=${userId}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, name })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.detail || 'Failed to initialize payment form';
      
      // Check for specific Stripe errors
      if (errorMessage.includes('card_declined') || errorMessage.includes('declined')) {
        throw new Error('Your card was declined. Please try a different payment method.');
      }
      if (errorMessage.includes('expired')) {
        throw new Error('This card has expired. Please use a different card.');
      }
      if (errorMessage.includes('insufficient_funds')) {
        throw new Error('Insufficient funds. Please try a different payment method.');
      }
      if (errorMessage.includes('invalid') || errorMessage.includes('incorrect')) {
        throw new Error('Invalid card details. Please check and try again.');
      }
      
      throw new Error(errorMessage);
    }
    
    return response.json();
  } catch (error) {
    // Network errors or other fetch failures
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
}

/**
 * Attach a payment method to billing profile
 * @param {string} userId - User ID
 * @param {string} paymentMethodId - Stripe PaymentMethod ID
 * @param {boolean} acceptBillingAgreement - Whether user accepts billing agreement
 * @param {string} [token] - Auth token
 */
export async function attachPaymentMethod(userId, paymentMethodId, acceptBillingAgreement, token) {
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_URL}/api/nfm/billing/payment-method/attach?user_id=${userId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      payment_method_id: paymentMethodId,
      accept_billing_agreement: acceptBillingAgreement
    })
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to attach payment method' }));
    throw new Error(error.detail || 'Failed to attach payment method');
  }
  
  return response.json();
}

/**
 * Remove payment method from billing profile
 * @param {string} userId - User ID
 * @param {string} [token] - Auth token
 */
export async function removePaymentMethod(userId, token) {
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_URL}/api/nfm/billing/payment-method?user_id=${userId}`, {
    method: 'DELETE',
    headers
  });
  
  if (!response.ok) {
    throw new Error('Failed to remove payment method');
  }
  
  return response.json();
}

/**
 * Check if user has valid payment method on file
 * @param {string} userId - User ID
 * @param {string} [token] - Auth token
 * @returns {Promise<boolean>}
 */
export async function hasValidPaymentMethod(userId, token) {
  try {
    const status = await getBillingProfileStatus(userId, token);
    return status.has_payment_method === true;
  } catch {
    return false;
  }
}
